import { Navigate, Outlet } from 'react-router-dom';

import useUserStore from '@/shared/store/user';

import routes from './constants/routesPath';

// Helper function to ensure list of lists format
const ensureListOfLists = (input) =>
  input.every(Array.isArray) ? input : [input];

function evaluateAccess({ user, route }) {
  const { permissions, roles, absoluteCheck } = route;
  // Convert permissions and roles to list of lists if necessary
  const normalizedPermissions = ensureListOfLists(permissions);
  const normalizedRoles = ensureListOfLists(roles);

  // Helper function to check user's permissions
  const hasPermission = (perm) => user.permissions?.includes(perm);
  // Helper function to check user's role
  const hasRole = (role) => user.role === role;

  // Adjusted AND logic to use hasPermission or hasRole
  const andLogic = (list, checker) => list.every(checker);
  // Adjusted OR logic to use hasPermission or hasRole
  const orLogic = (lists, checker) =>
    lists.some((list) => andLogic(list, checker));

  // Apply logic to permissions and roles
  const permissionsResult = orLogic(normalizedPermissions, hasPermission);
  const rolesResult = orLogic(normalizedRoles, hasRole);

  // Final evaluation based on absoluteCheck
  if (absoluteCheck) {
    // AND logic between permissions and roles
    return permissionsResult && rolesResult;
  } else {
    // OR logic between permissions and roles
    return permissionsResult || rolesResult;
  }
}

const Protected = ({ roles, permissions, absoluteCheck }) => {
  const { user } = useUserStore();
  // check if the user has the required roles and permissions
  const hasAccess = evaluateAccess({
    user,
    route: {
      permissions,
      roles,
      absoluteCheck,
    },
  });

  // if the user has the required roles and permissions, return the Outlet
  // otherwise, redirect the user to the home page
  return hasAccess ? <Outlet /> : <Navigate to={routes.HOME} replace />;
};

export default Protected;
